// String table for the kiosk SPA. Spanish is the primary locale (the venue
// and its audience are Chilean); English is offered for international
// visitors via the language toggle. Keys are flat; `{placeholders}` are
// substituted by `t()` in LangContext.

export type Lang = "es" | "en";

export const LANGS: Lang[] = ["es", "en"];

export const LOCALE: Record<Lang, string> = {
  es: "es-CL",
  en: "en-US",
};

// Native names shown on the language toggle / attract pills.
export const LANG_LABEL: Record<Lang, string> = {
  es: "Español",
  en: "English",
};

const ES = {
  // Generic actions
  back: "Volver",
  cancel: "Cancelar",
  continue: "Continuar",
  pay: "Pagar",
  tryAgain: "Intentar de nuevo",
  startOver: "Volver al inicio",
  callStaff: "Llamar al personal",
  retryPrint: "Reintentar impresión",
  free: "Gratis",
  add: "Sumar",
  remove: "Restar",

  // Shell
  stepDate: "Fecha",
  stepTickets: "Entradas",
  stepPayment: "Pago",
  stepDone: "Listo",

  // Attract / idle
  attractHeadline: "Compra tu entrada aquí",
  attractCta: "Toca para comenzar",
  attractHint: "Toca la pantalla",

  // Date
  dateTitle: "¿Qué día nos visitas?",
  dateSubtitle: "Elige la fecha de tu visita al museo",
  today: "Hoy",

  // Loading
  loadingTicketTypes: "Buscando entradas disponibles…",
  loadingCreatingOrder: "Creando tu orden…",
  loadingConfirming: "Confirmando el pago…",

  // Ticket selection
  ticketsTitle: "Elige tus entradas",
  ticketsForVisit: "Para tu visita",
  ticketsEmpty: "No hay entradas disponibles para esta fecha.",
  unavailable: "No disponible",
  total: "Total",
  ticketCountOne: "{n} entrada",
  ticketCountMany: "{n} entradas",

  // Review
  reviewTitle: "Revisa tu compra",
  reviewVisitDate: "Fecha de visita",
  reviewPayHint: "Pagarás con tarjeta en el terminal",

  // Awaiting card
  cardTitle: "Acerca o inserta tu tarjeta",
  cardAmountLabel: "Total a pagar",
  cardFollowReader: "Sigue las instrucciones en el terminal",

  // Card processing — status dots
  dotCard: "Tarjeta",
  dotPin: "Clave",
  dotAuth: "Autorizando",
  dotApproved: "Aprobada",
  cardProcessing: "Procesando pago…",

  // Printing
  printingTitle: "Imprimiendo tus entradas",
  printingWait: "Espera hasta que salgan todas",
  printingProgress: "{done} de {total}",

  // Done
  doneTitle: "¡Listo! Retira tus entradas",
  doneOrder: "Orden #{id}",
  doneEnjoy: "¡Disfruta tu visita!",
  ticketQrLabel: "Código de entrada {code}",

  // Payment failed
  payFailedTitle: "No pudimos procesar el pago",
  payFailedHint: "Prueba con otra tarjeta o pide ayuda en boletería.",
  hintForeignCard:
    "Es posible que este tótem no acepte tarjetas extranjeras o sin clave. Puedes pagar en boletería.",
  hintVerifyCharge:
    "Si se hizo un cargo en tu tarjeta, acércate a boletería y muestra el número de orden que aparece en pantalla.",
  confirmFailedReason: "No pudimos registrar tu pago.",
  orderNumberLabel: "Orden #{orderId}",

  // Print error
  printErrorTitle: "Problema al imprimir",
  printErrorStatus:
    "{done} de {total} entradas impresas · el pago ya fue cobrado",
  printErrorReassure:
    "Tu pago está seguro. El personal puede reimprimir las entradas que falten.",

  // Out of service
  oosTitle: "Tótem fuera de servicio",
  oosHint: "Por favor, dirígete a la boletería más cercana.",

  // IM30 0900 status messages, by code (see kiosk-shared im30.ts). In Spanish
  // the screen shows the terminal's own text; these are the English headlines.
  "im30.81": "Ingresa tu clave",
  "im30.82": "Autorizando…",
  "im30.83": "Selecciona una opción",
  "im30.84": "Acerca o inserta la tarjeta",
  "im30.85": "Selecciona las cuotas",
  "im30.86": "Ingresa las cuotas",
  "im30.87": "Confirma las cuotas",
  "im30.88": "Confirma las cuotas",
  "im30.93": "Consultando cuotas…",
} as const;

export type StringKey = keyof typeof ES;

const EN: Record<StringKey, string> = {
  back: "Back",
  cancel: "Cancel",
  continue: "Continue",
  pay: "Pay",
  tryAgain: "Try again",
  startOver: "Start over",
  callStaff: "Call staff",
  retryPrint: "Retry printing",
  free: "Free",
  add: "Add",
  remove: "Remove",

  stepDate: "Date",
  stepTickets: "Tickets",
  stepPayment: "Payment",
  stepDone: "Done",

  attractHeadline: "Buy your ticket here",
  attractCta: "Touch to start",
  attractHint: "Touch the screen",

  dateTitle: "Which day are you visiting?",
  dateSubtitle: "Pick the date of your museum visit",
  today: "Today",

  loadingTicketTypes: "Finding available tickets…",
  loadingCreatingOrder: "Creating your order…",
  loadingConfirming: "Confirming payment…",

  ticketsTitle: "Choose your tickets",
  ticketsForVisit: "For your visit",
  ticketsEmpty: "No tickets are available for this date.",
  unavailable: "Unavailable",
  total: "Total",
  ticketCountOne: "{n} ticket",
  ticketCountMany: "{n} tickets",

  reviewTitle: "Review your order",
  reviewVisitDate: "Visit date",
  reviewPayHint: "You'll pay by card on the terminal",

  cardTitle: "Tap or insert your card",
  cardAmountLabel: "Amount to pay",
  cardFollowReader: "Follow the instructions on the card reader",

  dotCard: "Card",
  dotPin: "PIN",
  dotAuth: "Authorizing",
  dotApproved: "Approved",
  cardProcessing: "Processing payment…",

  printingTitle: "Printing your tickets",
  printingWait: "Please wait until all tickets print",
  printingProgress: "{done} of {total}",

  doneTitle: "Done! Take your tickets",
  doneOrder: "Order #{id}",
  doneEnjoy: "Enjoy your visit!",
  ticketQrLabel: "Ticket code {code}",

  payFailedTitle: "We couldn't process the payment",
  payFailedHint: "Try another card or ask at the ticket desk.",
  hintForeignCard:
    "Foreign or PIN-less cards may not be accepted at this kiosk. You can pay at the ticket desk.",
  hintVerifyCharge:
    "If your card was charged, go to the ticket desk and show the order number on this screen.",
  confirmFailedReason: "We could not register your payment.",
  orderNumberLabel: "Order #{orderId}",

  printErrorTitle: "Printing problem",
  printErrorStatus:
    "{done} of {total} tickets printed · you've already been charged",
  printErrorReassure:
    "Your payment is safe. Staff can reprint any missing tickets.",

  oosTitle: "Kiosk out of service",
  oosHint: "Please go to the nearest ticket desk.",

  "im30.81": "Enter your PIN",
  "im30.82": "Authorizing…",
  "im30.83": "Choose an option",
  "im30.84": "Tap or insert your card",
  "im30.85": "Choose installments",
  "im30.86": "Enter installments",
  "im30.87": "Confirm installments",
  "im30.88": "Confirm installments",
  "im30.93": "Checking installments…",
};

export const STRINGS: Record<Lang, Record<StringKey, string>> = {
  es: ES,
  en: EN,
};

export function translate(
  lang: Lang,
  key: StringKey,
  params?: Record<string, string | number>
): string {
  const template = STRINGS[lang][key] ?? STRINGS.es[key] ?? String(key);
  if (!params) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in params ? String(params[name]) : `{${name}}`
  );
}
